import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { useT, useI18n } from "../i18n/I18nContext";
import { Mail, ArrowRight } from "lucide-react";

export default function ForgotPassword() {
  const t = useT();
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [devToken, setDevToken] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email, lang });
      setDone(true);
      setEmailSent(!!data.email_sent);
      if (data.dev_token) setDevToken(data.dev_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md card-mabw p-8 md:p-10 anim-fade-up" data-testid="forgot-card">
        <div className="flex items-center gap-3 mb-2">
          <Logo size={40} />
          <span className="font-semibold" style={{ fontFamily: "Outfit" }}>MABW</span>
        </div>

        {!done ? (
          <>
            <h1 className="text-3xl font-semibold mt-4" style={{ fontFamily: "Outfit" }}>
              {t("auth.forgot_title")}
            </h1>
            <p className="text-sm text-mabw-soft mt-2">{t("auth.forgot_sub")}</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <div>
                <label className="text-sm font-medium" htmlFor="fp-email">{t("auth.email")}</label>
                <input
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-mabw mt-1.5"
                  data-testid="fp-email-input"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="fp-error">
                  {error}
                </div>
              )}
              <button type="submit" disabled={busy} className="btn-mabw w-full" data-testid="fp-submit-btn">
                {busy ? t("auth.forgot_sending") : t("auth.forgot_submit")}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="fp-done">
            <div className="w-12 h-12 rounded-full bg-mabw-secondary/15 text-mabw-secondary flex items-center justify-center mt-4 mb-4">
              <Mail className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
              {t("auth.forgot_done_title")}
            </h1>
            <p className="text-sm text-mabw-soft mt-2">{t("auth.forgot_done_desc")}</p>

            {devToken && !emailSent && (
              <div className="mt-5 border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm">
                <div className="text-amber-800 font-medium mb-2">⚙️ {t("auth.forgot_dev_notice")}</div>
                <Link
                  to={`/reset-password?token=${encodeURIComponent(devToken)}`}
                  data-testid="fp-dev-reset-link"
                  className="inline-flex items-center gap-1.5 text-mabw-primary font-medium hover:underline"
                >
                  {t("auth.forgot_dev_link")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-sm text-mabw-soft">
          <Link to="/login" className="text-mabw-primary font-medium" data-testid="fp-back-login">
            ← {t("auth.back_to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
